import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Link,
  Typography,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { ExeReportHistoryStatus } from "../../components-new/layout/constants";
import { useGetErrorCodesQuery } from "../../useQuery";
import { useNavigate } from "react-router-dom";
import { formatDuration } from "../../utils/timeFormatter.js";
import { Visibility } from "@mui/icons-material";
import { AuthedFileLink } from "../../components-new/common";
const StyledTableCell = styled(TableCell)`
  color: #232d3d;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid #e2e6f3;
`;

const StyledTableBodyCell = styled(TableCell)`
  padding: 8px 20px;
  border-bottom: 1px solid #eee;
  p {
    color: #2b3539;
    font-size: 16px;
    font-weight: 500;
    padding-right: 15px;
    min-width: 60px;
  }
`;

const typeStyles = {
  PASS: {
    borderRadius: "41px",
    border: "0px solid #30ca17",
    backgroundColor: "#e7fbe4",
  },
  FAIL: {
    borderRadius: "41px",
    border: "0px solid #e15151",
    backgroundColor: "#fbe7e7",
  },
  ABEND: {
    borderRadius: "41px",
    backgroundColor: "#eef4fc",
  },
  ABORTED: {
    borderRadius: "41px",
    backgroundColor: "#fbe7e7",
  },
  RUNNING: {
    borderRadius: "41px",
    backgroundColor: "#fff3cd",
  },
  SKIPPED: {
    borderRadius: "41px",
    border: "0px solid #9e9e9e",
    backgroundColor: "#f5f5f5",
  },
};

const StatusPill = styled(Chip)(({ type }) => ({
  minWidth: "82px",
  padding: "8px 10px",
  gap: "10px",
  ...(typeStyles[type] || {}),
  "& span": {
    textTransform: "capitalize",
  },
}));

const FilterContainer = styled(Box)`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  align-items: center;
`;

const ExeTestCaseReportsTable = ({
  data,
  isSuccess,
  testSuiteName,
  runDate,
  tsrid,
  isTestSuite = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filteredData, setFilteredData] = useState([]);
  const [errorCodeFilter, setErrorCodeFilter] = useState("");
  const navigate = useNavigate();

  const { data: errorCodesResponse, isSuccess: isErrorCodesSuccess } = useGetErrorCodesQuery(
    tsrid,
    {
      enabled: Boolean(tsrid) && isTestSuite,
    },
  );

  // Extract error codes from the response
  const errorCodes = errorCodesResponse?.error_codes || errorCodesResponse;
  const hasErrorCodes = errorCodes && Object.keys(errorCodes).length > 0;

  useEffect(() => {
    if (data) {
      setFilteredData(data);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      if (errorCodeFilter === "") {
        setFilteredData(data);
      } else {
        const filtered = data.filter((item) => item.issuetype === errorCodeFilter);
        setFilteredData(filtered);
      }
      setPage(0);
    }
  }, [errorCodeFilter, data]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStepHistory = (e, item) => {
    e.preventDefault();
    let url;
    sessionStorage.setItem("logFileFromExeTestCaseReports", item.logfile);
    if (!testSuiteName) {
      url = `/new/reports?tcrid=${item.idtest_case_results}&tcname=${item.testcase}&tcRundate=${item.Executed_Date}`;
    } else {
      url = `/new/reports?tcrid=${item.idtest_case_results}&name=${testSuiteName}&date=${runDate}&tcname=${item.testcase}&tcRundate=${item.Executed_Date}`;
    }
    window.open(url, "_blank");
  };

  const handleDefectAnalysis = (e, item) => {
    e.preventDefault();
    if (item.Status === "FAIL") {
      const url = `/new/defect-analysis?tsrid=${item.idtest_case_results}&tcname=${item.testcase}&tcRundate=${item.Executed_Date}`;
      window.open(url, "_blank");
    }
  };

  const handleErrorCodeFilterChange = (event) => {
    setErrorCodeFilter(event.target.value);
  };

  const handleViewReport = (item) => {
    navigate(
      `/new/test-case-report?tcrid=${item.idtest_case_results}&tcname=${item.testcase}&tcRundate=${item.Executed_Date}`,
    );
  };

  const handleViewBenchmark = (item, mode) => {
    const url = `/new/reports/benchmark?tcrid=${item.idtest_case_results}&tcname=${encodeURIComponent(item.testcase)}&date=${encodeURIComponent(item.Executed_Date)}&mode=${mode}`;
    window.open(url, "_blank");
  };
 const handleOpenInStudio = (item) => {
  navigate("/new/test-studio", {
    state: {
      fromExecutionReports: true,
      idtest_case: item.Test_Case_Id,
      Test_Case: item.testcase,
    },
  });
};
  return (
    <Box flex={1}>
      {isSuccess && (
        <>
          {isTestSuite && isErrorCodesSuccess && (
            <FilterContainer>
              <Typography variant="subtitle1">Filter by Error Code:</Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="error-code-filter-label">Error Code</InputLabel>
                <Select
                  labelId="error-code-filter-label"
                  id="error-code-filter"
                  value={errorCodeFilter}
                  label="Error Code"
                  onChange={handleErrorCodeFilterChange}
                >
                  <MenuItem value="">
                    <em>{hasErrorCodes ? "All Error Codes" : "No Error Codes"}</em>
                  </MenuItem>
                  {hasErrorCodes &&
                    Object.entries(errorCodes).map(([code, details]) => (
                      <MenuItem key={code} value={code}>
                        {code} - {details.message} ({details.failedTestCasesCount})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </FilterContainer>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Run Date</StyledTableCell>
                  <StyledTableCell>Run By</StyledTableCell>
                  <StyledTableCell>Run Duration</StyledTableCell>
                  <StyledTableCell>Log File</StyledTableCell>
                  <StyledTableCell>Browser</StyledTableCell>
                  <StyledTableCell>Run Mode</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Error Code</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                  <StyledTableCell>Failure Analysis</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length ? (
                  filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => (
                      <TableRow key={index}>
                        <StyledTableBodyCell>{item.Executed_Date}</StyledTableBodyCell>
                        <StyledTableBodyCell>{item.Executed_By}</StyledTableBodyCell>
                        <StyledTableBodyCell>{formatDuration(item.Duration)}</StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <AuthedFileLink
                            url={item.logfile}
                            filename={`${item.testcase || "test-case"}.log`}
                            fallback={item.testcase}
                          >
                            {item.testcase}
                          </AuthedFileLink>
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>{item.Browser}</StyledTableBodyCell>
                        <StyledTableBodyCell>{item.runMode || "N/A"}</StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <StatusPill
                            label={ExeReportHistoryStatus[item.Status]}
                            type={item.Status}
                          />
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
                          {item.issuetype && (
                            <Chip
                              size="small"
                              label={item.issuetype}
                              color={item.issuetype === "E0000" ? "error" : "warning"}
                            />
                          )}
                                    <Tooltip title="Open in Studio">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenInStudio(item)}
                              sx={{
                                color: "#2550D1",
                                "&:hover": { bgcolor: "#e8edfb" },
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          </Box>
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <Box display="flex" gap={1} flexDirection="column">
                            {item.Status !== "ABORTED" && (
                              <Link
                                target="_blank"
                                sx={{ cursor: "pointer" }}
                                onClick={(e) => handleStepHistory(e, item)}
                              >
                                View test results
                              </Link>
                            )}

                            <Link
                              target="_blank"
                              sx={{ cursor: "pointer" }}
                              onClick={() => handleViewReport(item)}
                            >
                              View Report
                            </Link>

                            {item.multiRun && (
                              <Link
                                sx={{ cursor: "pointer", color: "#2550d1" }}
                                onClick={() => handleViewBenchmark(item, "multirun")}
                              >
                                View Multi-Run Results
                              </Link>
                            )}
                            {item.designPrincipals && (
                              <Link
                                sx={{ cursor: "pointer", color: "#2550d1" }}
                                onClick={() => handleViewBenchmark(item, "designprincipals")}
                              >
                                View Design Principal Results
                              </Link>
                            )}
                          </Box>
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <Link
                            sx={{
                              cursor: item.Status === "FAIL" ? "pointer" : "not-allowed",
                              color: item.Status === "FAIL" ? "#1976d2" : "grey",
                              pointerEvents: item.Status === "FAIL" ? "auto" : "none",
                              textDecoration: item.Status === "FAIL" ? "underline" : "none",
                            }}
                            onClick={(e) => item.Status === "FAIL" && handleDefectAnalysis(e, item)}
                          >
                            View Failure Analysis
                          </Link>
                        </StyledTableBodyCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <StyledTableBodyCell align="center" colSpan={10}>
                      No data available
                    </StyledTableBodyCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default ExeTestCaseReportsTable;
